<script setup lang="ts">
import { ref } from "vue";

import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";

const props = withDefaults(
  defineProps<{
    current: number | undefined | null;
    readonly?: boolean;
    ariaLabel?: string;
    size?: "sm" | "lg";
  }>(),
  { size: "sm", ariaLabel: undefined },
);
const emit = defineEmits<{ select: [value: number] }>();

const dropdownRef = ref<{ close: () => void }>();
const points = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function select(value: number) {
  emit("select", value);
  dropdownRef.value?.close();
}
</script>

<template>
  <DropdownMenu ref="dropdownRef" :readonly @click.stop>
    <template #trigger="{ isOpen }">
      <button
        class="trigger-button"
        :class="[props.size, { focused: isOpen }]"
        :disabled="readonly"
        :aria-label
        data-dropdown-trigger
      >
        <span class="trigger-value">{{ current ?? 0 }}</span>
      </button>
    </template>
    <div class="objective-points">
      <button
        v-for="point in points"
        :key="point"
        class="item"
        :class="{ active: point === current }"
        :aria-current="point === current"
        @click.stop="select(point)"
      >
        {{ point }}
      </button>
    </div>
  </DropdownMenu>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.trigger-button {
  border: 1px solid colors.$border-subtle;
  display: flex;
  justify-content: center;
  text-align: center;
  outline: revert;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &.sm {
    border-radius: 6px;
    width: 28px;
    height: 28px;
    line-height: 28px;

    &:hover:not(:disabled) {
      background-color: colors.$alpha-5;
    }

    .trigger-value {
      @include typography.small;
    }

    &.focused {
      border: 1px solid colors.$border-focused;
    }
  }

  &.lg {
    border-radius: 4px;
    width: 40px;
    height: 40px;
    line-height: 40px;

    &:hover:not(:disabled) {
      background-color: colors.$alpha-10;
    }

    .trigger-value {
      flex-grow: 1;
      font-size: font.$size-large;
      font-weight: font.$weight-bold;
    }

    &.focused {
      border: 1px solid colors.$border;
    }
  }
}

.objective-points {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  width: 102px;
  padding: 2px;

  .item {
    flex-grow: 1;
    background-color: colors.$alpha-5;
    border-radius: 4px;
    margin: 2px;
    height: 1.75rem;
    line-height: 1.75rem;
    width: 1.75rem;
    max-width: 1.75rem;
    text-align: center;
    font-size: font.$size-small;
    cursor: pointer;
    outline: revert;

    &:focus,
    &:hover {
      background-color: colors.$alpha-10;
    }

    &.active {
      color: colors.$text-inverse;
      background-color: colors.$main;
    }
  }
}
</style>
